import { useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import {
  listBehaviourArtifacts,
  deleteBehaviourArtifact,
  saveBehaviourArtifact,
  artifactVersion,
} from "../../lib/behaviourArtifactStore.js";
import { runArtifactOverBodies } from "../../lib/xrayArtifactRun.js";

// The executable tier's mount point: behaviour artifacts beside the declarative
// lenses, each runnable against the window the factory just scanned. The
// reading is the artifact's only truth surface in the browser — its
// occurrences pass the same emission contract the declarative tier passes, and
// the three failure classes (threw / invalid / empty) each get their own
// sentence because they are different diseases.

const CARD = "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)]";

function Chip({ label, onClick, tone = null, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="shrink-0 rounded-[2px] border px-[8px] py-[2px] text-[10px] uppercase leading-[1.2] tracking-[0.04em] transition-colors cursor-pointer disabled:cursor-default disabled:opacity-40"
      style={tone === "danger"
        ? { borderColor: "var(--red,#c0392b)", color: "var(--red,#c0392b)" }
        : { borderColor: "var(--line)", color: "var(--muted)" }}
    >
      {label}
    </button>
  );
}

function ArtifactRow({ artifact, bodies, onChanged }) {
  const [run, setRun] = useState({ status: "idle" });
  const [showSource, setShowSource] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [problems, setProblems] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const version = useMemo(() => artifactVersion(artifact), [artifact]);
  const claims = artifact.manifest?.claims || [];

  const execute = async () => {
    setRun({ status: "running" });
    try {
      const r = await runArtifactOverBodies(artifact, bodies);
      setRun({ status: "done", ...r });
    } catch (err) {
      setRun({ status: "done", ok: false, threw: err?.message || String(err), occurrences: [], invalid: [], windows: 0, emitted: 0 });
    }
  };

  const save = () => {
    let parsed;
    try {
      parsed = JSON.parse(draft);
    } catch (err) {
      setProblems([`not valid JSON: ${err.message}`]);
      return;
    }
    if (parsed.behaviour !== artifact.behaviour) {
      setProblems(["behaviour id cannot change here — unmount and draft again under the new id"]);
      return;
    }
    const r = saveBehaviourArtifact(null, parsed);
    if (!r.ok) { setProblems(r.problems); return; }
    setEditing(false);
    setProblems([]);
    onChanged();
  };

  return (
    <div className="grid min-w-0 gap-[5px] border-t border-[color:var(--line-soft)] px-[10px] py-[7px] first:border-t-0">
      <span className="flex min-w-0 flex-wrap items-baseline gap-[8px]">
        <span className="min-w-0 truncate font-mono text-[11px] text-[color:var(--ink)]">{artifact.behaviour}</span>
        <span
          className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
          style={{ borderColor: "var(--green)", color: "var(--ink)" }}
        >
          python lens
        </span>
        <span className="shrink-0 font-mono text-[10px] text-[color:var(--muted)]">{version}</span>
        <span className="ml-auto flex shrink-0 items-center gap-[6px]">
          {bodies?.length ? <Chip label={run.status === "running" ? "Running…" : "Run"} onClick={execute} disabled={run.status === "running"} /> : null}
          <Chip label={showSource ? "Hide source" : "Source"} onClick={() => setShowSource((v) => !v)} />
          {editing ? null : (
            <Chip
              label="Edit"
              onClick={() => { setDraft(JSON.stringify({ behaviour: artifact.behaviour, source: artifact.source, manifest: artifact.manifest }, null, 2)); setProblems([]); setEditing(true); }}
            />
          )}
          {confirming
            ? (
              <>
                <Chip label="Confirm unmount" tone="danger" onClick={() => { deleteBehaviourArtifact(null, artifact.behaviour); onChanged(); }} />
                <Chip label="Keep" onClick={() => setConfirming(false)} />
              </>
            )
            : <Chip label="Unmount" tone="danger" onClick={() => setConfirming(true)} />}
        </span>
      </span>

      <span className={NOTE_CLASS}>
        {artifact.manifest?.name} · reads: {artifact.manifest?.consumer}
        {claims.length ? ` · claims: ${claims.map((c) => c.name).join(", ")}` : ""}
      </span>
      {artifact.draftedFrom?.falsifier ? (
        <span className={NOTE_CLASS}>Invalid when: {artifact.draftedFrom.falsifier}</span>
      ) : null}

      {showSource ? (
        <pre className="m-0 max-h-[280px] overflow-auto rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[10.5px] leading-[1.5] text-[color:var(--muted)]">
          {artifact.source}
        </pre>
      ) : null}

      {run.status === "running" ? (
        <span className={`${NOTE_CLASS} animate-pulse`}>Running in the isolated Python realm…</span>
      ) : null}
      {run.status === "done" ? (
        <div className="grid min-w-0 gap-[4px]">
          {run.threw ? (
            <span className="whitespace-pre-wrap break-words font-mono text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>
              Error: {run.threw}
            </span>
          ) : (
            <span className={NOTE_CLASS}>
              {run.emitted} emitted over {run.windows} window{run.windows === 1 ? "" : "s"}
              {" "}→ {run.occurrences.length} contract-valid
              {run.invalid.length ? ` · ${run.invalid.length} INVALID` : ""}
              {run.emitted === 0 ? ". No matching behavior was found in this window." : ""}
            </span>
          )}
          {(run.invalid || []).slice(0, 3).map((r, i) => (
            <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>
              emitted[{r.index}]: {r.problems.join("; ")}
            </span>
          ))}
          {run.occurrences?.length ? (
            <pre className="m-0 max-h-[240px] overflow-auto rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[10px] leading-[1.5] text-[color:var(--muted)]">
              {JSON.stringify(run.occurrences.slice(0, 3), null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}

      {editing ? (
        <div className="grid min-w-0 gap-[5px] pt-[3px]">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={16}
            className="w-full min-w-0 resize-y rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[8px] py-[6px] font-mono text-[10.5px] leading-[1.5] text-[color:var(--ink)]"
          />
          {problems.map((p, i) => (
            <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
          ))}
          <span className="flex flex-wrap items-center gap-[8px]">
            <Chip label="Save" onClick={save} />
            <Chip label="Cancel" onClick={() => { setEditing(false); setProblems([]); }} />
            <span className={NOTE_CLASS}>
              Saving creates a new artifact version. New emissions will reference the new build.
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default function BehaviourArtifactsSection({ bodies = [] }) {
  const [tick, setTick] = useState(0);
  const artifacts = listBehaviourArtifacts();
  const onChanged = () => setTick((n) => n + 1);

  if (!artifacts.length) return null;
  return (
    <div className="grid min-w-0 gap-[6px]" data-tick={tick}>
      <span className={NOTE_CLASS}>
        {artifacts.length} Python lens{artifacts.length === 1 ? "" : "es"}. Each runs in an isolated realm and reports work through the emission contract. Run evaluates this window on demand.
      </span>
      <div className={`${CARD} grid min-w-0`}>
        {artifacts.map((a) => (
          <ArtifactRow key={a.behaviour} artifact={a} bodies={bodies} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}
